/**
 * `@dxf-ai-editor/cad-core` — per-INSTANCE Block (INSERT) helpers shared by
 * the definition walk (`blockDefinitionWalk.ts`), the public bounds entry
 * points (`blockGeometry.ts`) and the select path (`blockSelectGeometry.ts`):
 *
 *   - MINSERT-grid tiling of a single-instance AABB
 *     ({@link extendForMinsertGrid});
 *   - the synthetic INSERT that places a MULTILEADER's block content
 *     ({@link makeMleaderContentInsert});
 *   - the DYNAMIC per-instance side table (memo → (ancestor chain,
 *     instanceKey) → {@link DynamicInstanceEntry}: the evaluated-definition
 *     box, the retained evaluated child list and the per-instance ATTRIB
 *     resolution view);
 *   - the per-instance ATTRIB extent ({@link attributeInstanceBounds}).
 *
 * Sits BELOW the walk in the import graph (imports only `blockLookupContext`
 * types + leaf math), so the walk's Block-child arm and the top-level entry
 * point share ONE attribute/tiling truth without an import cycle.
 */

import {
  transformBoundsByInsert,
  mapBlockAttributeReferences,
  resolveAttributeContribution,
  type ALWAYS_INCLUDE,
  type BlockChildEntity,
  type BlockDefinition,
  type BlockInsertEntity,
  type MultileaderEntity,
} from "@dxf-ai-editor/drawing-model";

import type { BlockDefinitionBoundsMemo, DynamicInstanceChildren } from "./blockLookupContext";
import type { Aabb } from "./common";
import { boundsFromPoints, unionBounds, rotateDeg } from "./common";

/** Translate an AABB by `(dx, dy)`. */
function translateBounds(b: Aabb, dx: number, dy: number): Aabb {
  return { minX: b.minX + dx, minY: b.minY + dy, maxX: b.maxX + dx, maxY: b.maxY + dy };
}

/**
 * Extend a single-instance INSERT bounds into the full MINSERT grid bounds.
 * `columnCount`/`rowCount` default to 1 (a plain INSERT) — this is then a
 * no-op (the single grid corner (0,0) union'd with itself). Generic over any
 * `BlockInsertEntity` (a TOP-LEVEL insert or a NESTED one inside another
 * block definition) — a nested INSERT's own MINSERT grid is just as real
 * (Codex #467 P2).
 *
 * The grid-cell offset is scaled by `scaleX`/`scaleY` BEFORE rotating (Codex
 * #467 round-2 P2 "Scale MINSERT grid offsets with the INSERT") — matching
 * the renderer's own composed transform (`apps/web/src/app/scene/
 * blockMatrix.ts::composeBlockMatrix` = `T(insertion) × R(rotation) ×
 * S(scaleX,scaleY) × T(-basePoint)`; `blockRender.tsx::renderBlockInstance`
 * steps each MINSERT cell via `baseMatrix.translate({x: col*columnSpacing,
 * y: row*rowSpacing})`, which POST-multiplies the FULL composed matrix — so
 * the local `(col·columnSpacing, row·rowSpacing)` offset is transformed by
 * the matrix's linear part, i.e. `R(rotation) · S(scaleX,scaleY)`: SCALE
 * first, THEN rotate). The sibling single-instance transform
 * (`viewportMembership.ts::transformBoundsByInsert`) does the exact same
 * scale-then-rotate order for the block's own child geometry
 * (`lx = (cx - bx) * sx; ly = (cy - by) * sy;` then rotate) — column/row
 * spacing is specified in the SAME block-local coordinate system as the rest
 * of the block's geometry, so it is affected by the INSERT's scale factors
 * identically (Autodesk DXF reference, INSERT Array fields: group 44/45
 * spacing values are block-local, not pre-scaled world distances). The
 * PRE-fix code rotated the RAW (unscaled) spacing — correct only for
 * `scaleX === scaleY === 1` — silently under/over-reporting the MINSERT
 * grid's bounds/grips/select extent for any non-unit-scaled MINSERT.
 */
export function extendForMinsertGrid(single: Aabb, entity: BlockInsertEntity): Aabb {
  const cols = Math.max(1, Math.round(entity.columnCount));
  const rows = Math.max(1, Math.round(entity.rowCount));
  if (cols <= 1 && rows <= 1) return single;

  const colTotal = (cols - 1) * entity.columnSpacing;
  const rowTotal = (rows - 1) * entity.rowSpacing;
  const rotation = entity.rotation ?? 0;
  const sx = entity.scaleX;
  const sy = entity.scaleY;

  let out = single;
  // The three additional grid corners besides (0,0) — already baked into
  // `single`. Column/row spacing directions are the INSERT's own
  // scaled-then-rotated local X/Y axes.
  const corners: Array<[number, number]> = [
    [colTotal, 0],
    [0, rowTotal],
    [colTotal, rowTotal],
  ];
  for (const [lx, ly] of corners) {
    const { x: dx, y: dy } = rotateDeg(lx * sx, ly * sy, rotation);
    out = unionBounds(out, translateBounds(single, dx, dy));
  }
  return out;
}

/**
 * The MULTILEADER fields that place its BLOCK content (contentType 1).
 *
 * `blockAttributeReferences` (DXF group 330/177/44/302 — Codex PR #784 round
 * 9 P2 "Carry MLEADER block attributes into content bounds") is part of the
 * Pick: the render adapter maps those references onto the synthetic INSERT's
 * `attributes[]` before flattening (`apps/web/…/adapters/typed/
 * multileaderFromEntity.ts`: `attributes: mapBlockAttributeReferences(entity.
 * blockAttributeReferences ?? [], safeRecordLookup(blocks, blockContentName))`
 * → `buildMultileaderGeometry` → `flattenBlockContent({… attributes})`), so a
 * reference that MOVES an attribute or replaces a short ATTDEF default with
 * longer text is REAL drawn geometry. A ref-less reduced type made
 * {@link makeMleaderContentInsert} emit an INSERT with no rows at all, so
 * bounds fell back to the row-less ATTDEF template box and the rendered
 * attribute text could sit outside the enclosing INSERT metadata AABB.
 */
export type MleaderBlockContentRef = Pick<
  MultileaderEntity,
  "blockName" | "blockScale" | "blockRotation" | "textLocation" | "blockAttributeReferences"
>;

/**
 * The synthetic single-instance INSERT that places a MULTILEADER's BLOCK
 * content (contentType 1): the referenced block AT `textLocation`, uniform-
 * scaled by `blockScale`, rotated by `blockRotation` (degrees), carrying the
 * MLEADER's own block-attribute overrides as `attributes[]` — exactly how
 * both live renderers flatten it (`scene/renderMultileader.tsx` +
 * `scene3d/dimensions/multileaderToPrimitives.ts`, both through the shared
 * `geom/multileaderGeometry.ts`). One truth shared by the MLEADER
 * block-content BOUNDS (`blockGeometry.ts`) and SELECT
 * (`blockSelectGeometry.ts`) resolvers (never re-spelt).
 *
 * `def` is the REFERENCED definition (own-property-safe lookup by the
 * caller; `undefined` for a missing/unresolvable name). The handle→tag
 * resolution is NOT re-derived here — it is `drawing-model`'s
 * `mapBlockAttributeReferences`, the SAME function the render adapter calls,
 * so both agree regime by regime: pass 1 claims each still-unclaimed ref
 * whose `attDefHandle` equals an ATTDEF's own `handle` (in block-definition
 * ATTDEF order), pass 2 falls back to a ref whose `attDefHandle` is a real
 * ATTDEF TAG (legacy/malformed back-pointer), and a ref matching NEITHER — or
 * any ref at all when `def` is `undefined` (no ATTDEFs to claim against) — is
 * DROPPED rather than synthesized as a garbage-tag attribute. Dropping is
 * exactly what the renderer does with such a ref too, so the INSERT then
 * resolves against the ATTDEF templates alone, unchanged from the pre-fix
 * behaviour for that case.
 */
export function makeMleaderContentInsert(e: MleaderBlockContentRef, def: BlockDefinition | undefined): BlockInsertEntity {
  return {
    type: "Block",
    blockName: e.blockName,
    insertionPoint: { x: e.textLocation.x, y: e.textLocation.y, z: 0 },
    scaleX: e.blockScale,
    scaleY: e.blockScale,
    scaleZ: 1,
    rotation: e.blockRotation,
    columnCount: 1,
    rowCount: 1,
    columnSpacing: 0,
    rowSpacing: 0,
    attributes: mapBlockAttributeReferences(e.blockAttributeReferences ?? [], def),
  } as unknown as BlockInsertEntity;
}

/* ── Row-less ATTDEF template memo ──────────────────────────────────────── */

/**
 * A memo slot for a definition's ROW-LESS ATTDEF template-fallback box
 * (block-LOCAL; `null` when the definition has no visible VARIABLE ATTDEF;
 * `undefined` while not yet resolved). A STATIC definition's slot lives in
 * {@link rowlessTemplateBoxByMemo} (per memo, per name); a DYNAMIC instance's
 * slot is its own {@link DynamicInstanceEntry} (per memo, per ancestor chain +
 * instance state — the template anchors are the EVALUATED ATTDEFs').
 */
interface RowlessTemplateSlot {
  rowlessTemplate?: Aabb | null;
}

/**
 * Block name → the STATIC definition's row-less template slot, held PER
 * `BlockDefinitionBoundsMemo` instance (a `WeakMap` keyed by the memo object
 * itself) so the entry shares the memo's exact lifetime and validity contract
 * — valid only for the `blockDefinitions` table the memo was populated
 * against, dropped together with it. This is what keeps the documented "a
 * memo hit never re-walks the definition" guarantee
 * (`blockGeometry.memo.test.ts`) intact for the attribute-less common case:
 * the template resolution runs once per (memo, name), not once per INSERT
 * (Codex PR #784 round 2 P3 — the previous boolean flag still re-ran the
 * per-INSERT `resolveAttributeContribution` walk for every row-less INSERT of
 * a definition WITH variable ATTDEFs). A caller with no memo
 * (`resolveBlockInsertBoundsOrNull` without `definitionBoundsMemo`) gets a
 * fresh per-call map — one resolution per call, the same cost class as the
 * definition walk that call performs anyway.
 *
 * Instance-agnostic by construction: with NO rows only the "no matching row"
 * template regime of `resolveAttributeContribution` fires (no WORLD
 * overrides, no orphans ⇒ `overrideBypassPoints` is empty and every point is
 * block-LOCAL), and that regime reads the ATTDEF templates alone. A constant
 * ATTDEF is excluded on purpose — it has no per-instance row and its
 * `defaultValue` box is part of the memoized definition footprint
 * (`childEntityBounds` → `constantAttdefLocalCorners` ∪ `attdefBounds`).
 */
const rowlessTemplateBoxByMemo = new WeakMap<BlockDefinitionBoundsMemo, Map<string, RowlessTemplateSlot>>();

function staticRowlessTemplateSlot(memo: BlockDefinitionBoundsMemo, name: string): RowlessTemplateSlot {
  let slots = rowlessTemplateBoxByMemo.get(memo);
  if (!slots) {
    slots = new Map();
    rowlessTemplateBoxByMemo.set(memo, slots);
  }
  let slot = slots.get(name);
  if (!slot) {
    slot = {};
    slots.set(name, slot);
  }
  return slot;
}

/** The row-less template box of `def`, resolved once per `slot`. */
function rowlessTemplateLocalBounds(slot: RowlessTemplateSlot, def: BlockDefinition): Aabb | null {
  if (slot.rowlessTemplate !== undefined) return slot.rowlessTemplate;
  const box = boundsFromPoints(resolveAttributeContribution([], def).liveLocalPoints);
  slot.rowlessTemplate = box;
  return box;
}

/* ── DYNAMIC per-instance side table ────────────────────────────────────── */

/**
 * One DYNAMIC definition evaluated for ONE (ancestor chain, instance state)
 * — the value of {@link dynamicBoundsByMemo}.
 */
export interface DynamicInstanceEntry extends RowlessTemplateSlot {
  /**
   * A one-entry `{name → block-LOCAL AABB | ALWAYS_INCLUDE}` map — exactly
   * the `resolved` argument the shared `resolveDefinitionWithCycleGuard`
   * expects (keyed by definition name), so the dynamic arm of
   * `resolveInsertLocalBounds` reuses the identical memoize + cycle-guard
   * skeleton the static arm does — one truth, no second guard — while the
   * memo hit is keyed by (chain, instance state) instead of by name alone.
   */
  readonly resolved: Map<string, Aabb | typeof ALWAYS_INCLUDE>;
  /**
   * The EVALUATED child list of this instance, retained from the memo-miss
   * evaluation (`[]` until then — a cycle-guarded entry is never read past
   * its ALWAYS_INCLUDE box). The renderer places a template-following
   * ATTRIB at the EVALUATED ATTDEF's anchor (`attdefFromEntity.ts::
   * resolveTemplateAnchor` over `blockFromEntity.ts`'s `evaluatedChildren`
   * loop; the evaluator translates ATTDEF anchors — `block-evaluator/
   * geometry.ts::mapTextAnchors`), so the instance's ATTRIB rows must resolve
   * against THESE anchors, not the authored ones
   * ({@link instanceAttributeDefinition}).
   */
  evaluated: readonly BlockChildEntity[];
  /**
   * Has {@link instanceEvaluatedChildren} already run the evaluation for this
   * entry? A separate flag (not `evaluated.length > 0`) because an EMPTY
   * evaluated list is a legitimate result (every child hidden by the active
   * visibility state), and re-running the evaluator for it would defeat the
   * memo the entry exists for.
   */
  evaluatedReady?: boolean;
  /** Lazily built {@link instanceAttributeDefinition} view. */
  attributeDef?: BlockDefinition;
}

/**
 * The EVALUATED children of this dynamic instance — the ONE evaluation site
 * (Codex PR #784 round 9 P2 "Evaluate dynamic state for block select
 * geometry"). Both consumers of a dynamic instance's children go through it,
 * so they can never disagree about what the INSERT renders:
 *
 *   - the BOUNDS walk (`blockDefinitionWalk.ts::resolveInsertLocalBounds`,
 *     inside its cycle guard's accumulate callback), and
 *   - the SELECT walk (`blockSelectGeometry.ts::insertSelectGeometry`).
 *
 * Whichever runs first pays for the evaluation; the other reads the retained
 * list. Memoized on the ENTRY, which is itself keyed by (memo object,
 * ancestor chain, instance state) — see {@link dynamicBoundsByMemo} — so a
 * move / stretch / visibility / array action evaluates once per distinct
 * (chain, state) per `blocks` table for BOTH walks together, exactly the
 * renderer's own `[parentChain, buildEvaluateBlockCacheKey(...)]` keying.
 */
export function instanceEvaluatedChildren(
  entry: DynamicInstanceEntry,
  dynamic: DynamicInstanceChildren,
  parentChain: readonly string[],
): readonly BlockChildEntity[] {
  if (!entry.evaluatedReady) {
    entry.evaluated = dynamic.children(parentChain);
    entry.evaluatedReady = true;
  }
  return entry.evaluated;
}

/**
 * Per-INSTANCE evaluated-definition side table (Codex PR #784 round 3 P1):
 * memo object → `JSON.stringify([parentChain, instanceKey])` →
 * {@link DynamicInstanceEntry}.
 *
 * Keyed by the memo OBJECT (a `WeakMap`, like {@link rowlessTemplateBoxByMemo})
 * so the table shares the memo's exact lifetime + validity contract
 * (`BlockLookupContext.definitionBoundsMemo`: valid only for the `blocks`
 * table it was populated against; replace the memo object to invalidate). A
 * caller with no memo gets a fresh per-call table — one evaluation per
 * distinct state per call, the cost class of the walk that call performs
 * anyway. Never touches the static memo: a dynamic definition's name is NOT
 * written there, so a later static resolution of the same name (a caller
 * without the hook) cannot read an instance-specific box.
 *
 * The ancestor definition-name chain is PART of the key — the renderer's own
 * `[parentChain, buildEvaluateBlockCacheKey(...)]` keying
 * (`blockFromEntity.ts::resolveEvaluatedChildren`): the evaluator's result
 * depends on the chain through its nested-depth guard (`evaluateBlock`
 * returns the AUTHORED list, un-evaluated, when `parentChain.length >
 * MAX_NESTED_DYNAMIC_DEPTH`), so a (definition, state) first met past that
 * depth must NOT memoize its un-evaluated box for a later shallow INSERT of
 * the same state. MINSERT cells, sibling nested instances and every
 * top-level INSERT (chain `[]`) still share one entry per state.
 */
const dynamicBoundsByMemo = new WeakMap<BlockDefinitionBoundsMemo, Map<string, DynamicInstanceEntry>>();

export function dynamicInstanceEntry(
  memo: BlockDefinitionBoundsMemo,
  parentChain: readonly string[],
  instanceKey: string,
): DynamicInstanceEntry {
  let byKey = dynamicBoundsByMemo.get(memo);
  if (!byKey) {
    byKey = new Map();
    dynamicBoundsByMemo.set(memo, byKey);
  }
  const key = JSON.stringify([parentChain, instanceKey]);
  let entry = byKey.get(key);
  if (!entry) {
    // Stryker disable next-line ArrayDeclaration: the placeholder is overwritten by the evaluated list before any read — `instanceEvaluatedChildren` sets `evaluated` the first time either walk asks for this instance's children, and the only entry neither walk ever asks is one whose name is already in the ancestor chain (`parentChain` IS `inProgress`, so the same (chain, key) entry always hits the cycle guard / the select walk's own `inProgress.has` short-circuit), which every consumer drops at its ALWAYS_INCLUDE bounds (or `null` select geometry) before `attributeInstanceBounds` could read it. Pinned by `blockGeometry.dynamic.test.ts` (cycle + missing-definition cases).
    entry = { resolved: new Map(), evaluated: [] };
    byKey.set(key, entry);
  }
  return entry;
}

/**
 * The definition AS THIS DYNAMIC INSTANCE RENDERS IT for ATTRIB resolution:
 * the AUTHORED child order (so `resolveAttributeContribution`'s per-tag
 * occurrence-index matching stays fixed against the full unfiltered ATTDEF
 * list — `blockFromEntity.ts` "Occurrence-index precompute"), with each
 * child replaced by its EVALUATED counterpart (same `id`) where one exists —
 * so a Move/Stretch-relocated ATTDEF supplies its evaluated anchor to the
 * row's text box. A child ABSENT from the evaluated list (visibility-hidden)
 * keeps its authored entity: its row still contributes a box the renderer
 * does not draw — an over-inclusion, the package's conservative-bounds
 * convention; never an under-report.
 */
export function instanceAttributeDefinition(entry: DynamicInstanceEntry, def: BlockDefinition): BlockDefinition {
  if (entry.attributeDef) return entry.attributeDef;
  const evaluatedById = new Map<string, BlockChildEntity>();
  for (const child of entry.evaluated) evaluatedById.set(child.id, child);
  const view: BlockDefinition = { ...def, entities: def.entities.map((child) => evaluatedById.get(child.id) ?? child) };
  entry.attributeDef = view;
  return view;
}

/* ── Per-instance ATTRIB extent ─────────────────────────────────────────── */

/**
 * Per-INSTANCE ATTRIB extent of an INSERT (Codex PR #784 P1 — "include instance
 * ATTRIB geometry in INSERT metadata bounds"): the definition footprint alone
 * is NOT the INSERT's extent. AutoCAD substitutes each INSERT's OWN
 * `attributes[]` values for the definition's variable ATTDEFs and honours the
 * per-instance EATTEDIT placement (`overridePosition` / height / rotation), so
 * a title-block instance with a longer value, or an ATTRIB moved outside the
 * definition footprint, extends the reference's real extent (ZOOM E, select,
 * cull) beyond anything the definition-only box can know.
 *
 * ONE TRUTH — the resolution is NOT re-derived here: `drawing-model`'s
 * `resolveAttributeContribution` (`wcsBoundsBlock.ts`, the `entityBounds`
 * facet's own INSERT resolver) already mirrors the renderer
 * (`scene3d/adapters/typed/attdefFromEntity.ts`) regime by regime — tag +
 * per-tag occurrence matching (duplicate ATTDEF tags), constant-ATTDEF stray
 * discard, invisible override, the WORLD `overridePosition` regime (bypasses
 * THIS insert's own affine, DXF-group defaults 2.5 / 0), the block-LOCAL
 * template regime (rides the affine, justification-aware anchor), the orphan
 * block-origin fallback, the no-row `defaultValue || tag || "?"` template
 * fallback, embedded-newline stacking and `kind: "attribute"` annotative
 * contexts — using the established `height × widthFactor × 0.6` glyph-width
 * heuristic (`wcsBoundsText.ts`, the same estimate `mtextBoxSize` uses here).
 *
 * This helper only maps that result into cad-core's AABB contract:
 *   - `liveLocalPoints` are in the block-LOCAL frame → AABB → the SAME
 *     `transformBoundsByInsert` the definition AABB gets (conservative
 *     rotated-box superset, cad-core's documented Block contract);
 *   - `overrideBypassPoints` are already in the frame the INSERT lives in
 *     (WORLD for a top-level INSERT; the ENCLOSING definition's local frame
 *     for a nested Block child, where the caller's ancestor transform still
 *     applies) → AABB, NOT re-transformed by this insert's affine;
 *   - both tiled across this INSERT's MINSERT grid (`extendForMinsertGrid`),
 *     exactly as `blockInsertWcsBounds` tiles `overrideBypassPoints` and as
 *     the renderer shifts a WORLD attribute per cell (`blockFromEntity.ts`).
 *
 * `def` is the referenced definition (defined — the caller returned for a
 * missing name). For a DYNAMIC instance (`instance` given) the rows resolve
 * against {@link instanceAttributeDefinition} — the EVALUATED ATTDEF anchors
 * the renderer places a template-following ATTRIB at — and the row-less
 * template box is memoized on the instance entry; a STATIC definition's rows
 * resolve against the authored definition and its row-less box is memoized
 * per (memo, name).
 *
 * Instance-specific by construction, so it is NEVER memoized in the
 * per-definition-name `BlockDefinitionBoundsMemo` — the memo keeps holding
 * the instance-agnostic definition box; for a NESTED Block child the child's
 * own `attributes[]` are static definition content, so folding them into the
 * enclosing definition's memoized box is correct (same reasoning as
 * `blockInsertWcsBounds` round-6 finding-2). `null` when the INSERT carries no
 * visible attribute geometry (no rows AND no uncovered variable ATTDEF).
 */
export function attributeInstanceBounds(
  entity: BlockInsertEntity,
  def: BlockDefinition,
  memo: BlockDefinitionBoundsMemo,
  instance: DynamicInstanceEntry | undefined,
): Aabb | null {
  const attributes = entity.attributes ?? [];
  const attributeDef = instance ? instanceAttributeDefinition(instance, def) : def;
  let live: Aabb | null;
  let bypass: Aabb | null = null;
  if (attributes.length === 0) {
    // The overwhelmingly common attribute-less INSERT: its only possible text
    // is the definition's row-less template fallback, memoized per (memo,
    // name) for a static definition / per instance entry for a dynamic one —
    // and, crucially, NO per-INSERT walk of `def.entities` on the memo-hit
    // path (the metadata index resolves EVERY INSERT of a drawing against
    // one memo; the definition-bounds memo contract is "a memo hit never
    // re-walks the definition").
    live = rowlessTemplateLocalBounds(instance ?? staticRowlessTemplateSlot(memo, entity.blockName), attributeDef);
  } else {
    const { liveLocalPoints, overrideBypassPoints } = resolveAttributeContribution(attributes, attributeDef);
    live = boundsFromPoints(liveLocalPoints);
    bypass = boundsFromPoints(overrideBypassPoints);
  }
  let out: Aabb | null = null;
  if (live) {
    const placed = transformBoundsByInsert(live, entity, def.basePoint);
    // Stryker disable next-line ConditionalExpression: `transformBoundsByInsert` returns null only for non-finite corners (overflow to ±Infinity), and `live` is built by `boundsFromPoints`, which already drops non-finite points — unreachable for any physically meaningful coordinate (overflow to ±Infinity only for |coordinates| ~1e306); the null arm is a type-narrowing guard.
    if (placed) out = extendForMinsertGrid(placed, entity);
  }
  if (bypass) {
    const tiled = extendForMinsertGrid(bypass, entity);
    out = out ? unionBounds(out, tiled) : tiled;
  }
  return out;
}
